import React, { useState } from 'react';
import ResourceEditorModal from './ResourceEditorModal';
import ResourceAvailabilityModal from './ResourceAvailabilityModal';
import { guessTimezone, RESOURCE_TYPE_LABELS } from './bookingFormat';
import { useAuth } from '../../../helpers/AuthContext';

/**
 * Resource (calendar) manager — the top-down CRUD view of a seller's bookable
 * units. Semi-independent: give it a `resources` collection (from
 * useBookingResources) and it handles create / edit / delete plus, when
 * `onManageAvailability` is provided, a hook into the availability editor.
 */
export default function ResourceManager({
    resources = [],
    loading = false,
    error = null,
    onCreate,
    onUpdate,
    onDelete,
    onEnsureDefault,
    onReload,
    onResourceChange,
    defaultTimezone = null,
}) {
    const { channel } = useAuth();
    const [editing, setEditing] = useState(null); // resource object or {} for new
    const [availabilityResource, setAvailabilityResource] = useState(null);
    const [busyId, setBusyId] = useState(null);
    const [actionError, setActionError] = useState(null);
    const [copiedId, setCopiedId] = useState(null);

    const publicBookingUrl = resource => (
        channel?.url
            ? `${channel.url.replace(/\/$/, '')}/book/${resource.uuid}`
            : `/book/${resource.uuid}`
    );

    const copyPublicLink = async (resource) => {
        setActionError(null);
        try {
            await window.navigator.clipboard.writeText(publicBookingUrl(resource));
            setCopiedId(resource.id);
            window.setTimeout(() => setCopiedId(null), 2000);
        } catch (err) {
            setActionError('Could not copy the link. Open it and copy the address instead.');
        }
    };

    const handleSubmit = async (payload) => {
        if (editing && editing.id) {
            await onUpdate(editing.id, payload);
        } else {
            await onCreate(payload);
        }
    };

    const handleDelete = async (resource) => {
        const activeMsg = 'Delete this calendar? Calendars with existing bookings are '
            + 'deactivated instead of deleted so history is preserved.';
        if (!window.confirm(activeMsg)) return;
        setActionError(null);
        setBusyId(resource.id);
        try {
            await onDelete(resource.id);
        } catch (err) {
            setActionError(err.message || 'Could not delete this calendar.');
        } finally {
            setBusyId(null);
        }
    };

    const handleEnsureDefault = async () => {
        setActionError(null);
        try {
            await onEnsureDefault();
        } catch (err) {
            setActionError(err.message || 'Could not create a default calendar.');
        }
    };

    if (loading) {
        return (
            <div className="booking-state booking-state--loading">
                <i className="fas fa-spinner fa-spin" aria-hidden="true"></i>
                <span>Loading calendars…</span>
            </div>
        );
    }

    if (error) {
        return (
            <div className="booking-state booking-state--error" role="alert">
                <div className="error">{error}</div>
                {onReload && (
                    <button type="button" className="button secondary" onClick={onReload}>Try again</button>
                )}
            </div>
        );
    }

    return (
        <div className="booking-resource-manager">
            <div className="booking-pane-header">
                <div>
                    <h3>Calendars &amp; resources</h3>
                    <p className="help">
                        Each calendar is a bookable unit — a staff member, a room, a rental unit,
                        or your pickup counter. Availability is authored per calendar.
                    </p>
                </div>
                <button type="button" className="button primary" onClick={() => setEditing({})}>
                    <i className="fas fa-plus" aria-hidden="true"></i> New calendar
                </button>
            </div>

            {actionError && <div className="error booking-inline-error">{actionError}</div>}

            {resources.length === 0 ? (
                <div className="booking-empty">
                    <i className="fas fa-calendar-alt" aria-hidden="true"></i>
                    <h4>No calendars yet</h4>
                    <p>Create a calendar to start authoring availability and taking bookings.</p>
                    <div className="booking-empty-actions">
                        <button type="button" className="button primary" onClick={() => setEditing({})}>
                            Create a calendar
                        </button>
                        {onEnsureDefault && (
                            <button type="button" className="button secondary" onClick={handleEnsureDefault}>
                                Use a default calendar
                            </button>
                        )}
                    </div>
                </div>
            ) : (
                <ul className="booking-resource-list">
                    {resources.map(resource => (
                        <li
                            key={resource.id}
                            className={[
                                'booking-resource-card',
                                resource.isActive ? '' : 'is-inactive',
                                resource.id === availabilityResource?.id ? 'is-selected' : '',
                            ].filter(Boolean).join(' ')}
                        >
                            <span
                                className="booking-resource-color"
                                style={{ backgroundColor: resource.color || '#4f46e5' }}
                                aria-hidden="true"
                            />
                            <div className="booking-resource-main">
                                <div className="booking-resource-title">
                                    <strong>{resource.name}</strong>
                                    {!resource.isActive && <span className="booking-badge muted">Inactive</span>}
                                    {resource.settings?.publicBooking?.enabled && (
                                        <span className="booking-badge ok">Public booking</span>
                                    )}
                                    {resource.type === 'pickup'
                                        && resource.settings?.localPickup?.enabled !== false && (
                                        <span className="booking-badge ok">Pickup scheduling</span>
                                    )}
                                </div>
                                <div className="booking-resource-meta">
                                    <span>{RESOURCE_TYPE_LABELS[resource.type] || resource.type}</span>
                                    <span aria-hidden="true">·</span>
                                    <span>Capacity {resource.capacity}</span>
                                    <span aria-hidden="true">·</span>
                                    <span>{resource.timezone}</span>
                                    {resource.location && (
                                        <>
                                            <span aria-hidden="true">·</span>
                                            <span>{resource.location}</span>
                                        </>
                                    )}
                                </div>
                                {resource.settings?.publicBooking?.enabled && resource.isActive && (
                                    <div className="booking-public-link">
                                        <a
                                            href={publicBookingUrl(resource)}
                                            target="_blank"
                                            rel="noreferrer"
                                        >
                                            Open public booking page
                                        </a>
                                        <button
                                            type="button"
                                            className="booking-linkbtn"
                                            onClick={() => copyPublicLink(resource)}
                                        >
                                            {copiedId === resource.id ? 'Copied' : 'Copy link'}
                                        </button>
                                    </div>
                                )}
                            </div>
                            <div className="booking-resource-actions">
                                <button
                                    type="button"
                                    className="button secondary small"
                                    onClick={() => setAvailabilityResource(resource)}
                                >
                                    <i className="fas fa-clock" aria-hidden="true"></i>
                                    <span className="booking-btn-label"> Availability</span>
                                </button>
                                <button
                                    type="button"
                                    className="button secondary small"
                                    onClick={() => setEditing(resource)}
                                >
                                    <i className="fas fa-pen" aria-hidden="true"></i>
                                    <span className="booking-btn-label"> Edit</span>
                                </button>
                                <button
                                    type="button"
                                    className="button danger small"
                                    onClick={() => handleDelete(resource)}
                                    disabled={busyId === resource.id}
                                >
                                    <i className="fas fa-trash" aria-hidden="true"></i>
                                    <span className="booking-btn-label"> Delete</span>
                                </button>
                            </div>
                        </li>
                    ))}
                </ul>
            )}

            {editing && (
                <ResourceEditorModal
                    resource={editing.id ? editing : null}
                    defaultTimezone={defaultTimezone || channel?.timezone || guessTimezone()}
                    onSubmit={handleSubmit}
                    onClose={() => setEditing(null)}
                />
            )}

            {availabilityResource && (
                <ResourceAvailabilityModal
                    resource={availabilityResource}
                    onSaved={(updated) => {
                        setAvailabilityResource(updated);
                        if (onResourceChange) onResourceChange(updated);
                    }}
                    onClose={() => setAvailabilityResource(null)}
                />
            )}
        </div>
    );
}
